import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { getUserId } from "../backend/auth";
import { isBackendConfigured } from "../backend/client";
import { fetchFriendsLeaderboard, fetchLeaderboard, fetchMyEntry, LeaderboardEntry } from "../backend/leaderboard";
import { CAMPAIGN_MAPS } from "../maps/maps";
import { THEME, panel } from "../ui/theme";
import { addCastleBackdrop, addContentPanel, addMenuBackdrop, addMenuHeader, addQuitButton, beginSceneLifecycle, menuButton } from "../ui/menuUi";
import { drawAvatar, formatNameWithTitle } from "../ui/avatar";
import { sound } from "../systems/Sound";

const DIFFICULTIES = ["SQUIRE", "KNIGHT", "LORD"] as const;
const MODES = ["ALL", "FRIENDS"] as const;
type LeaderboardMode = typeof MODES[number];

export class LeaderboardScene extends Phaser.Scene {
  private mapIdx = 0;
  private difficulty = 2; // Default to Knight
  private mode: LeaderboardMode = "ALL";
  
  // UI objects
  private mapText!: Phaser.GameObjects.Text;
  private mapIndexText!: Phaser.GameObjects.Text;
  private mapImage!: Phaser.GameObjects.Image;
  private statusText!: Phaser.GameObjects.Text;
  private refreshButton?: Phaser.GameObjects.Container;
  
  // Table groups / containers
  private podiumGroup!: Phaser.GameObjects.Group;
  private scrollContainer!: Phaser.GameObjects.Container;
  private anchorContainer!: Phaser.GameObjects.Container;
  
  private scrollRowsCount = 0;
  private requestVersion = 0;
  private lifecycle!: ReturnType<typeof beginSceneLifecycle>;

  constructor() { super("LeaderboardScene"); }

  create(): void {
    this.lifecycle = beginSceneLifecycle(this);
    addMenuBackdrop(this, { darkness: 0.77 });
    addContentPanel(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, "iron", 0.97);
    addCastleBackdrop(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, { veil: "dark" });
    addMenuHeader(this, {
      title: "LEADERBOARDS",
      subtitle: "Legends are written one defended realm at a time.",
      onBack: () => goTo(this, "MenuScene"),
    });
    addQuitButton(this);
    
    this.lifecycle.key("keydown-ESC", () => goTo(this, "MenuScene"));
    this.lifecycle.key("keydown-LEFT", () => this.changeMap(-1));
    this.lifecycle.key("keydown-RIGHT", () => this.changeMap(1));

    // Groups & Containers for table rows
    this.podiumGroup = this.add.group();
    this.scrollContainer = this.add.container(0, 0);
    this.anchorContainer = this.add.container(0, 0);

    // Setup mask for scroll container
    const maskShape = this.make.graphics({});
    maskShape.fillRect(58, 408, GAME_WIDTH - 116, 200);
    const mask = maskShape.createGeometryMask();
    this.scrollContainer.setMask(mask);

    // Scroll wheel input listener (restricted to table boundaries)
    const wheel = (pointer: Phaser.Input.Pointer, _go: unknown, _dx: number, dy: number) => {
      if (pointer.x >= 58 && pointer.x <= GAME_WIDTH - 58 && pointer.y >= 408 && pointer.y <= 608) {
        const listH = 200;
        const rowH = 40;
        const maxScroll = Math.max(0, this.scrollRowsCount * rowH - listH);
        const scrollY = Phaser.Math.Clamp(this.scrollContainer.y - dy * 0.5, -maxScroll, 0);
        this.scrollContainer.y = scrollY;
      }
    };
    this.lifecycle.input("wheel", wheel as (...args: unknown[]) => void);

    this.drawFilters();
    this.drawTableShell();

    if (!isBackendConfigured()) {
      this.statusText.setText("Online leaderboards are not configured for this build.");
      return;
    }
    
    void this.refresh();
  }

  private drawFilters(): void {
    const map = CAMPAIGN_MAPS[this.mapIdx];
    const w = GAME_WIDTH - 116;
    
    // Consolidated filter panel (height 120px)
    panel(this, 58, 100, w, 120, "parchment").setAlpha(0.94);
    
    // Map Carousel: Centered at x = 200, y = 160
    menuButton(this, 96, 160, 40, 32, "‹", { 
      name: "leaderboard-map-prev", 
      fontSize: 22, 
      onClick: () => this.changeMap(-1) 
    });
    menuButton(this, 304, 160, 40, 32, "›", { 
      name: "leaderboard-map-next", 
      fontSize: 22, 
      onClick: () => this.changeMap(1) 
    });
    
    // Gold frame border for the map picture
    const border = this.add.graphics();
    border.lineStyle(1.5, THEME.hex.goldDim, 0.8);
    border.strokeRoundedRect(126, 118, 148, 85, 4);
    
    // Map preview image
    const textureKey = `map-preview-${map.key}`;
    this.mapImage = this.add.image(200, 160, this.textures.exists(textureKey) ? textureKey : "px-white")
      .setDisplaySize(144, 81);
    
    this.mapText = this.add.text(200, 112, map.name.toUpperCase(), { 
      fontFamily: THEME.fonts.display, 
      fontSize: "14px", 
      color: THEME.colors.iron 
    }).setOrigin(0.5);
    
    this.mapIndexText = this.add.text(200, 208, `${this.mapIdx + 1} / ${CAMPAIGN_MAPS.length}`, { 
      fontFamily: THEME.fonts.body, 
      fontSize: "11px", 
      color: THEME.colors.lock 
    }).setOrigin(0.5);

    // Difficulty Segmented Control (centered at x = 610)
    this.add.text(610, 120, "DIFFICULTY", {
      fontFamily: THEME.fonts.display,
      fontSize: "13px",
      color: THEME.colors.lock
    }).setOrigin(0.5);

    this.drawSegmentedControl(430, 144, 360, 36, DIFFICULTIES, this.difficulty - 1, (_label, index) => {
      this.difficulty = index + 1;
      void this.refresh();
    });

    // Mode Segmented Control (centered at x = 1020)
    this.add.text(1020, 120, "MODE", {
      fontFamily: THEME.fonts.display,
      fontSize: "13px",
      color: THEME.colors.lock
    }).setOrigin(0.5);

    this.drawSegmentedControl(900, 144, 240, 36, MODES, this.mode === "ALL" ? 0 : 1, (label) => {
      this.mode = label;
      void this.refresh();
    });
  }

  private drawSegmentedControl<T extends string>(
    x: number,
    y: number,
    w: number,
    h: number,
    options: readonly T[],
    initialIndex: number,
    onClick: (value: T, index: number) => void
  ): void {
    const slotW = (w - 6) / options.length;
    
    // Backing track
    const track = this.add.graphics();
    track.fillStyle(THEME.hex.ironDeep, 0.72);
    track.fillRoundedRect(x, y, w, h, 6);
    track.lineStyle(1.5, THEME.hex.ironEdge, 0.6);
    track.strokeRoundedRect(x, y, w, h, 6);
    
    // Active background position helper
    const getSegmentX = (idx: number) => x + 3 + idx * slotW + 2;
    
    // Active background container
    const activeBgContainer = this.add.container(getSegmentX(initialIndex), y + 3);
    const activeBg = this.add.graphics();
    activeBg.fillStyle(THEME.hex.gold, 1);
    activeBg.fillRoundedRect(0, 0, slotW - 4, h - 6, 4);
    activeBgContainer.add(activeBg);
    
    const texts: Phaser.GameObjects.Text[] = [];
    let currentActiveIdx = initialIndex;
    
    const updateIndex = (newIdx: number) => {
      currentActiveIdx = newIdx;
      this.tweens.add({
        targets: activeBgContainer,
        x: getSegmentX(newIdx),
        duration: 150,
        ease: "Power2.easeOut"
      });
      texts.forEach((txt, idx) => {
        txt.setColor(idx === newIdx ? THEME.colors.ironDeep : THEME.colors.parchmentDim);
      });
    };
    
    options.forEach((opt, idx) => {
      const slotX = x + 3 + idx * slotW;
      const optCenterX = slotX + slotW / 2;
      const optCenterY = y + h / 2;
      
      const text = this.add.text(optCenterX, optCenterY, opt, {
        fontFamily: THEME.fonts.display,
        fontSize: "13px",
        color: idx === initialIndex ? THEME.colors.ironDeep : THEME.colors.parchmentDim
      }).setOrigin(0.5);
      texts.push(text);
      
      const zone = this.add.zone(optCenterX, optCenterY, slotW, h)
        .setInteractive({ useHandCursor: true });
        
      zone.on("pointerdown", () => {
        if (idx === currentActiveIdx) return;
        sound.playUi("click");
        updateIndex(idx);
        onClick(opt, idx);
      });
      
      zone.on("pointerover", () => {
        if (idx !== currentActiveIdx) {
          text.setColor(THEME.colors.gold);
        }
      });
      
      zone.on("pointerout", () => {
        if (idx !== currentActiveIdx) {
          text.setColor(THEME.colors.parchmentDim);
        }
      });
    });
  }

  private drawTableShell(): void {
    const x = 58, y = 230, w = GAME_WIDTH - 116, h = 424;
    panel(this, x, y, w, h, "parchment").setAlpha(0.94);
    
    // Align headers with row columns
    const headers: Array<[string, number, number]> = [
      ["RANK", x + 32, 0], 
      ["COMMANDER", x + 144, 0], 
      ["SCORE", x + w - 290, 1], 
      ["BEST TIME", x + w - 166, 1], 
      ["WINS", x + w - 42, 1]
    ];
    headers.forEach(([label, px, origin]) => {
      this.add.text(px, y + 20, label, { 
        fontFamily: THEME.fonts.display, 
        fontSize: "13px", 
        color: THEME.colors.lock 
      }).setOrigin(origin, 0.5);
    });
    
    this.add.rectangle(x + 22, y + 36, w - 44, 1.5, THEME.hex.goldDim, 0.5).setOrigin(0);
    
    this.statusText = this.add.text(GAME_WIDTH / 2, y + h / 2, "Loading records…", { 
      fontFamily: THEME.fonts.body, 
      fontSize: "18px", 
      color: THEME.colors.lock 
    }).setOrigin(0.5);
    
    this.refreshButton = menuButton(this, GAME_WIDTH / 2, 678, 190, 40, "REFRESH RECORDS", { 
      name: "leaderboard-refresh", 
      fontSize: 14, 
      display: true, 
      onClick: () => void this.refresh() 
    });
  }

  private changeMap(direction: number): void {
    this.mapIdx = Phaser.Math.Wrap(this.mapIdx + direction, 0, CAMPAIGN_MAPS.length);
    const map = CAMPAIGN_MAPS[this.mapIdx];
    this.mapText.setText(map.name.toUpperCase());
    this.mapIndexText.setText(`${this.mapIdx + 1} / ${CAMPAIGN_MAPS.length}`);
    
    // Update map image
    const textureKey = `map-preview-${map.key}`;
    if (this.textures.exists(textureKey)) {
      this.mapImage.setTexture(textureKey);
      this.mapImage.setDisplaySize(144, 81);
      this.mapImage.setVisible(true);
    } else {
      this.mapImage.setVisible(false);
    }
    
    void this.refresh();
  }

  private async refresh(): Promise<void> {
    const version = ++this.requestVersion;
    const button = this.refreshButton;
    button?.setAlpha(0.6);
    const background = button?.getData("bg") as Phaser.GameObjects.NineSlice | undefined;
    background?.disableInteractive();
    
    this.statusText.setText("Loading records…");
    this.podiumGroup.clear(true, true);
    this.scrollContainer.removeAll(true);
    this.anchorContainer.removeAll(true);
    this.scrollContainer.y = 0;
    this.scrollRowsCount = 0;

    try {
      const map = CAMPAIGN_MAPS[this.mapIdx];
      const [entries, mine] = await Promise.all([
        this.mode === "FRIENDS" 
          ? fetchFriendsLeaderboard(map.key, this.difficulty, 50) 
          : fetchLeaderboard(map.key, this.difficulty, 50),
        fetchMyEntry(map.key, this.difficulty),
      ]);
      
      if (!this.scene.isActive() || version !== this.requestVersion) return;
      
      if (!entries.length) {
        this.statusText.setText(
          this.mode === "FRIENDS" 
            ? "No friends have claimed this realm yet." 
            : "No entries yet — be the first to defend this realm!"
        );
      } else {
        this.statusText.setText("");
        this.renderRows(entries, mine);
      }
    } catch (err) {
      console.error("[leaderboard] refresh error", err);
      if (this.scene.isActive() && version === this.requestVersion) {
        this.statusText.setText("Records could not be reached. Please try again.");
      }
    } finally {
      if (this.scene.isActive() && version === this.requestVersion && button?.active) {
        button.setAlpha(1);
        background?.setInteractive({ useHandCursor: true });
      }
    }
  }

  private renderRows(entries: LeaderboardEntry[], mine: LeaderboardEntry | null): void {
    const x = 58, w = GAME_WIDTH - 116;
    const myId = getUserId();
    
    // 1. Render Podium (top 3 entries)
    const podiumCount = Math.min(entries.length, 3);
    for (let index = 0; index < podiumCount; index++) {
      const entry = entries[index];
      const me = !!myId && entry.user_id === myId;
      
      let rowY = 0;
      let rowH = 0;
      let cardBgColor = 0;
      let cardBgAlpha = 0;
      let outlineColor = 0;
      let outlineAlpha = 0;
      let outlineWidth = 1;
      let nameColor: string = THEME.colors.iron;
      let avatarSize = 40;
      
      if (index === 0) {
        rowY = 266;
        rowH = 50;
        cardBgColor = THEME.hex.gold;
        cardBgAlpha = 0.12;
        outlineColor = THEME.hex.gold;
        outlineAlpha = 0.7;
        outlineWidth = 2;
        nameColor = THEME.colors.goldDim;
      } else if (index === 1) {
        rowY = 316;
        rowH = 46;
        cardBgColor = THEME.hex.iron;
        cardBgAlpha = 0.08;
        outlineColor = 0xb8c4c8;
        outlineAlpha = 0.4;
      } else {
        rowY = 362;
        rowH = 46;
        cardBgColor = THEME.hex.iron;
        cardBgAlpha = 0.08;
        outlineColor = 0xcd7f32;
        outlineAlpha = 0.3;
      }
      
      const bgHex = me ? THEME.hex.goldDim : cardBgColor;
      const bgAlpha = me ? 0.25 : cardBgAlpha;
      
      // Background row rect
      const card = this.add.rectangle(x + 22, rowY, w - 44, rowH, bgHex, bgAlpha).setOrigin(0);
      this.podiumGroup.add(card);
      
      // Card border
      const border = this.add.graphics();
      border.lineStyle(me ? 2 : outlineWidth, me ? THEME.hex.gold : outlineColor, me ? 0.8 : outlineAlpha);
      border.strokeRoundedRect(x + 22, rowY, w - 44, rowH, 6);
      this.podiumGroup.add(border);
      
      const centerY = rowY + rowH / 2;
      
      // Medal
      const medal = this.drawMedal(x + 48, centerY, index + 1);
      this.podiumGroup.add(medal);
      
      // Avatar (40px)
      const avatar = drawAvatar(this, x + 110, centerY, avatarSize, entry.avatar_id, entry.user_id);
      this.podiumGroup.add(avatar);
      
      // Commander name
      const nameTxt = this.add.text(x + 144, centerY, formatNameWithTitle(entry.display_name, entry.avatar_id).slice(0, 30), {
        fontFamily: THEME.fonts.body,
        fontSize: index === 0 ? "16px" : "15px",
        color: me ? THEME.colors.goldDim : nameColor,
        fontStyle: me ? "bold" : "normal"
      }).setOrigin(0, 0.5);
      this.podiumGroup.add(nameTxt);
      
      // Stats
      const scoreTxt = this.add.text(x + w - 290, centerY, entry.best_score.toLocaleString(), {
        fontFamily: THEME.fonts.body,
        fontSize: index === 0 ? "16px" : "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.podiumGroup.add(scoreTxt);
      
      const timeTxt = this.add.text(x + w - 166, centerY, formatTime(entry.best_time_ms), {
        fontFamily: THEME.fonts.body,
        fontSize: index === 0 ? "16px" : "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.podiumGroup.add(timeTxt);
      
      const winsTxt = this.add.text(x + w - 42, centerY, String(entry.wins), {
        fontFamily: THEME.fonts.body,
        fontSize: index === 0 ? "16px" : "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.podiumGroup.add(winsTxt);
    }
    
    // 2. Render Scrollable Rows (Rank 4 onwards)
    const scrollEntries = entries.slice(3);
    this.scrollRowsCount = scrollEntries.length;
    
    scrollEntries.forEach((entry, idx) => {
      const globalIndex = idx + 3;
      const rowY = 408 + idx * 40;
      const me = !!myId && entry.user_id === myId;
      
      const bgHex = me ? THEME.hex.goldDim : THEME.hex.iron;
      const bgAlpha = me ? 0.25 : globalIndex % 2 === 0 ? 0.12 : 0.06;
      
      const rowBg = this.add.rectangle(x + 22, rowY, w - 44, 38, bgHex, bgAlpha).setOrigin(0);
      this.scrollContainer.add(rowBg);
      
      if (me) {
        const border = this.add.graphics();
        border.lineStyle(1.5, THEME.hex.goldDim, 0.8);
        border.strokeRoundedRect(x + 22, rowY, w - 44, 38, 4);
        this.scrollContainer.add(border);
      }
      
      const centerY = rowY + 19;
      
      // Rank label
      const rankTxt = this.add.text(x + 48, centerY, `#${globalIndex + 1}`, {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.lock
      }).setOrigin(0.5);
      this.scrollContainer.add(rankTxt);
      
      // Avatar (32px)
      const avatar = drawAvatar(this, x + 110, centerY, 32, entry.avatar_id, entry.user_id);
      this.scrollContainer.add(avatar);
      
      // Name
      const nameTxt = this.add.text(x + 144, centerY, formatNameWithTitle(entry.display_name, entry.avatar_id).slice(0, 30), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron,
        fontStyle: me ? "bold" : "normal"
      }).setOrigin(0, 0.5);
      this.scrollContainer.add(nameTxt);
      
      // Stats
      const scoreTxt = this.add.text(x + w - 290, centerY, entry.best_score.toLocaleString(), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.scrollContainer.add(scoreTxt);
      
      const timeTxt = this.add.text(x + w - 166, centerY, formatTime(entry.best_time_ms), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.scrollContainer.add(timeTxt);
      
      const winsTxt = this.add.text(x + w - 42, centerY, String(entry.wins), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.scrollContainer.add(winsTxt);
    });
    
    // 3. Draw fixed Anchor Row ("Your best") if not in top 8
    const isUserInTop8 = entries.slice(0, 8).some(entry => !!myId && entry.user_id === myId);
    
    if (mine && !isUserInTop8) {
      const rowY = 608;
      const rowH = 40;
      
      const line = this.add.rectangle(x + 22, rowY, w - 44, 1.5, THEME.hex.goldDim, 0.5).setOrigin(0);
      this.anchorContainer.add(line);
      
      const card = this.add.rectangle(x + 22, rowY + 1.5, w - 44, rowH - 1.5, THEME.hex.goldDim, 0.25).setOrigin(0);
      this.anchorContainer.add(card);
      
      const border = this.add.graphics();
      border.lineStyle(1.5, THEME.hex.goldDim, 0.8);
      border.strokeRoundedRect(x + 22, rowY + 1.5, w - 44, rowH - 1.5, 4);
      this.anchorContainer.add(border);
      
      const centerY = rowY + rowH / 2;
      
      // Determine rank in the top 50 list
      const myIdxInList = entries.findIndex(entry => !!myId && entry.user_id === myId);
      const myRankStr = myIdxInList !== -1 ? `#${myIdxInList + 1}` : "#—";
      
      const rankTxt = this.add.text(x + 48, centerY, myRankStr, {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.goldDim,
        fontStyle: "bold"
      }).setOrigin(0.5);
      this.anchorContainer.add(rankTxt);
      
      const avatar = drawAvatar(this, x + 110, centerY, 32, mine.avatar_id, mine.user_id);
      this.anchorContainer.add(avatar);
      
      const nameTxt = this.add.text(x + 144, centerY, formatNameWithTitle(mine.display_name, mine.avatar_id).slice(0, 30), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron,
        fontStyle: "bold"
      }).setOrigin(0, 0.5);
      this.anchorContainer.add(nameTxt);
      
      const scoreTxt = this.add.text(x + w - 290, centerY, mine.best_score.toLocaleString(), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.anchorContainer.add(scoreTxt);
      
      const timeTxt = this.add.text(x + w - 166, centerY, formatTime(mine.best_time_ms), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.anchorContainer.add(timeTxt);
      
      const winsTxt = this.add.text(x + w - 42, centerY, String(mine.wins), {
        fontFamily: THEME.fonts.body,
        fontSize: "15px",
        color: THEME.colors.iron
      }).setOrigin(1, 0.5);
      this.anchorContainer.add(winsTxt);
    }
  }

  private drawMedal(x: number, y: number, rank: number): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    
    let fillColor = 0xffd66b;
    let borderColor = 0xd8a030;
    let radius = 13;
    
    if (rank === 1) {
      fillColor = 0xffd66b;
      borderColor = 0xd8a030;
      radius = 15;
    } else if (rank === 2) {
      fillColor = 0xd8e4e8;
      borderColor = 0x90a0a8;
    } else if (rank === 3) {
      fillColor = 0xcd7f32;
      borderColor = 0x905020;
    }
    
    const circle = this.add.graphics();
    circle.fillStyle(fillColor, 1);
    circle.fillCircle(0, 0, radius);
    circle.lineStyle(1.5, borderColor, 1);
    circle.strokeCircle(0, 0, radius);
    
    const text = this.add.text(0, 0, String(rank), {
      fontFamily: THEME.fonts.display,
      fontSize: rank === 1 ? "14px" : "13px",
      color: THEME.colors.ironDeep,
      fontStyle: "bold"
    }).setOrigin(0.5);
    
    container.add([circle, text]);
    return container;
  }
}

function formatTime(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
